import React, { useEffect, useState } from "react";
import Countdown from "react-countdown";
import { Link, useNavigate } from "react-router-dom";
import LazyLoad from "react-lazyload";
import { useSelector } from "react-redux";
import {
  IoBookOutline,
  IoHeart,
  IoHeartOutline,
  IoImageOutline,
  IoMusicalNotesOutline,
  IoVideocamOutline,
} from "react-icons/io5";

import config from "../../lib/config";
import { resolveNftMedia } from "../../lib/nftMedia";
import BSC from "../../assets/images/BNB12.png";
import ETH from "../../assets/images/eth.png";
import LazyLoader from "../lazyloader";
import Avatars from "../../views/Avatar";
import isEmpty from "../../lib/isEmpty";
import { getrating, likes } from "../../actions/v1/token";
import { network } from "../../views/network";
import NftAudio from "../../components/NftAudio";
import NftVideo from "../../components/NftVideo";
import "./NftCard.css";

const abbreviateAddress = (address) => {
  const value = String(address || "");
  return value.length > 12 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
};

export default function TokenCard({ item, LikedTokenList = [], curraddress = "" }) {
  const walletDetails = useSelector((state) => state.wallet_connect_context);
  const navigate = useNavigate();
  const [starRate, setStarRate] = useState(0);
  const [likeList, setLikeList] = useState(LikedTokenList || []);

  const walletAddress = walletDetails?.UserAccountAddr || curraddress;
  const tokenCount = item?.tokenCounts;

  useEffect(() => {
    let isCurrent = true;

    const loadCardActivity = async () => {
      if (tokenCount === null || tokenCount === undefined || tokenCount === "") return;

      try {
        const rating = await getrating({ tokenCounts: tokenCount });
        const average = rating?.data?.data?.data?.[0]?.avgQuantity;
        if (isCurrent && average !== null && average !== undefined) {
          setStarRate(Math.round(average));
        }

        if (walletAddress) {
          const likeResponse = await likes({
            currAdd: walletAddress,
            tokenCount,
          });
          const nextLikes = likeResponse?.data?.data?.data;
          if (isCurrent && !isEmpty(nextLikes)) setLikeList(nextLikes);
        }
      } catch (error) {
        // The card remains usable when optional rating or like metadata is unavailable.
      }
    };

    loadCardActivity();
    return () => {
      isCurrent = false;
    };
  }, [tokenCount, walletAddress]);

  if (!item) return null;

  const tokenOwner = item.tokenowners_current || {};
  const nftMedia = resolveNftMedia(item, {
    backUrl: config.Back_URL,
    ipfsImageUrl: config.IPFS_IMG,
  });
  const isAudioNFT = nftMedia.kind === "audio";
  const isVideoNFT = nftMedia.kind === "video";
  const isBookNFT = nftMedia.kind === "book";
  const mediaType = isBookNFT
    ? "Book"
    : isAudioNFT
      ? "Audio"
      : isVideoNFT
        ? "Video"
        : "Image";
  const mediaIcon = isBookNFT ? (
    <IoBookOutline aria-hidden="true" />
  ) : isAudioNFT ? (
    <IoMusicalNotesOutline aria-hidden="true" />
  ) : isVideoNFT ? (
    <IoVideocamOutline aria-hidden="true" />
  ) : (
    <IoImageOutline aria-hidden="true" />
  );

  const nftInfoUrl = `/info/${item.tokenOwner}/${item.contractAddress}/${tokenCount}`;
  const openNftDetails = () => navigate(nftInfoUrl);
  const title = item.tokenName || "Untitled NFT";

  const tokenDisplayUrl = () => {
    if (item.Preview) {
      const canViewOriginal =
        tokenOwner?.WhiteListUser?.includes(walletDetails?.UserAccountAddr) ||
        tokenOwner?.tokenOwner === walletDetails?.UserAccountAddr;

      if (!canViewOriginal) {
        return `${config.Back_URL}/preview/${item.tokenCreator}/${item.Preview}`;
      }
    }

    return nftMedia.originalUrl;
  };

  const imageSource = nftMedia.coverUrl || config.AudioImg;
  const imageFallback = nftMedia.coverUrl || config.AudioImg;
  const handleImageError = (event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") return;

    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = imageFallback;
  };

  const hasHighestBid =
    item.higheBd?.tokenBidAmt !== null && item.higheBd?.tokenBidAmt !== undefined;
  const priceLabel = hasHighestBid ? "Highest bid" : "Token price";
  const priceValue = hasHighestBid
    ? item.higheBd.tokenBidAmt
    : tokenOwner.tokenPrice;
  const currencyIcon =
    String(item.SelectedNetwork) === String(network.BSC.Chainid) ? BSC : ETH;
  const currencyName =
    String(item.SelectedNetwork) === String(network.BSC.Chainid) ? "BNB" : "ETH";

  const creator = item.tokenCreatorInfo || {};
  const creatorAddress = creator.curraddress || item.tokenCreator || "";
  const creatorName =
    creator.name || item.tokenuser?.name || abbreviateAddress(creatorAddress) || "Unknown creator";
  const creatorUrl = creator.customurl
    ? `/${creator.customurl}`
    : `/user/${creatorAddress}`;
  const isLiked = likeList?.some((likedToken) => likedToken.tokenCounts === tokenCount);

  const formatTime = (time) => String(time).padStart(2, "0");
  const renderTimer = ({ hours, minutes, seconds, completed }) =>
    completed ? (
      <span>Auction completed</span>
    ) : (
      <span aria-label={`${hours} hours, ${minutes} minutes, ${seconds} seconds remaining`}>
        {formatTime(hours)}:{formatTime(minutes)}:{formatTime(seconds)}
      </span>
    );

  const renderMedia = () => {
    if (isVideoNFT) {
      return (
        <NftVideo
          compact
          src={tokenDisplayUrl()}
          mediaName={item.image}
          mimeType={item.mediaContentType}
          poster={
            nftMedia.coverUrl || undefined
          }
          label={title}
        />
      );
    }

    if (isAudioNFT) {
      return (
        <NftAudio
          compact
          className="nft-card__image"
          src={tokenDisplayUrl()}
          cover={nftMedia.coverUrl || config.AudioImg}
          mimeType={item.mediaContentType}
          label={title}
          preload="none"
        />
      );
    }

    if (isBookNFT) {
      return (
        <img
          src={
            item.thumb
              ? `${config.Back_URL}/Thumb_compressedImage/${item.tokenCreator}/${item.thumb}`
              : config.AudioImg
          }
          className="nft-card__image"
          loading="lazy"
          alt={`${title} cover`}
        />
      );
    }

    return (
      <img
        src={imageSource}
        onError={handleImageError}
        className="nft-card__image"
        loading="lazy"
        alt={`${title} NFT artwork`}
      />
    );
  };

  return (
    <article className="tokens tokencard_tok nft-card">
      <div className="baner_image nft-card__surface">
        <div className="ethactios nft-card__media">
          <div
            className="token-media-link nft-card__media-link"
            role={isVideoNFT ? undefined : "link"}
            tabIndex={isVideoNFT ? undefined : 0}
            aria-label={isVideoNFT ? undefined : `View ${title}`}
            onClick={isVideoNFT ? undefined : openNftDetails}
            onKeyDown={(event) => {
              if (isVideoNFT) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openNftDetails();
              }
            }}
          >
            <LazyLoad
              height={280}
              placeholder={<LazyLoader />}
              offset={[-200, 0]}
              debounce={300}
            >
              {renderMedia()}
            </LazyLoad>
          </div>

          {tokenOwner.clocktime && tokenOwner.endclocktime && (
            <div className="timers nft-card__timer">
              {new Date(tokenOwner.endclocktime) > Date.now() ? (
                <Countdown
                  date={new Date(tokenOwner.endclocktime)}
                  autoStart
                  onStart={() => new Date(tokenOwner.clocktime)}
                  renderer={renderTimer}
                />
              ) : (
                <span>Auction completed</span>
              )}
            </div>
          )}

          <span className="nft-card__type-badge">
            {mediaIcon}
            {mediaType}
          </span>
        </div>

        <div className="auctioncards nft-card__body">
          <div className="nft-card__heading">
            <Link className="nft-card__title-link" to={nftInfoUrl} title={title}>
              <h3>{title}</h3>
            </Link>
            <div
              className="nft-card__rating"
              aria-label={`Rated ${starRate} out of 5 stars`}
              title={`${starRate} out of 5 stars`}
            >
              {[1, 2, 3, 4, 5].map((value) => (
                <i
                  key={`${tokenCount}-star-${value}`}
                  className={`fa fa-star${starRate >= value ? " is-active" : ""}`}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>

          <div className="highestbid nft-card__price-row">
            <div>
              <span className="nft-card__eyebrow">{priceLabel}</span>
              <strong className="nft-card__price">
                {priceValue !== null && priceValue !== undefined && priceValue !== ""
                  ? priceValue
                  : "Not listed"}
              </strong>
            </div>
            <div className="nft-card__currency" title={currencyName}>
              <img src={currencyIcon} alt="" />
              <span>{hasHighestBid && item.higheBd?.CoinName ? item.higheBd.CoinName : currencyName}</span>
            </div>
          </div>

          <div className="abaucdetail nft-card__footer">
            <Link className="nft-card__creator" to={creatorUrl} title={`Creator: ${creatorName}`}>
              <span className="nft-card__avatar">
                {creator.image ? (
                  <img
                    src={`${config.Back_URL}/images/${creator._id}/${creator.image}`}
                    alt=""
                  />
                ) : (
                  <Avatars />
                )}
              </span>
              <span className="nft-card__creator-copy">
                <small>Creator</small>
                <strong>@{creatorName}</strong>
              </span>
            </Link>

            <button
              type="button"
              className={`hearting nft-card__like${isLiked ? " is-liked" : ""}`}
              aria-label={isLiked ? `View liked NFT ${title}` : `View NFT ${title}`}
              onClick={openNftDetails}
            >
              {isLiked ? <IoHeart aria-hidden="true" /> : <IoHeartOutline aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
